/* Party Social — Event Drinks Calculator
   ---------------------------------------------------------------------------
   CONSUMPTION MODEL — standard hosted-event assumptions
     Total servings      = guests x (rate x hours + 0.6 arrival allowance)
     Rate by event style : brunch 0.8 | lunch 0.8 | dinner 1.1 | cocktail 1.4 | wedding 1.3
     Welcome sparkling   : brunch 0.6 | wedding 1.0 glasses per guest (the toast)
     Warm-weather uplift : x1.3 on soft drinks when outdoors
     Water               : 0.45 L per guest per hour outdoors, 0.30 L indoors
     Ice                 : (guests x hours) / 3 kg
     Bottle conversions  : wine 5 glasses | sparkling 6 | spirits 16 | mixers 8 per litre

   GLASSWARE MODEL - Party Social house standard
     Glassware is quoted PER GUEST, not per pour. A guest who has four drinks
     over an evening does not get four glasses: they keep one, swap it when
     they change drink, and a share of the room never touches wine/beer/tea
     at all. So the totals are budgeted, then split across glass types in
     proportion to what is actually being poured.

     STANDING / cocktail-table events (the common case):
       2.20 glasses per guest, calibrated against what Party Social actually
       sends: for 50 guests over 4 hours on a full bar that is about
       18 red / 18 white / 45 hi-ball / 18 beer / 10 rocks / 6 martini.
       Washing and re-using during the event takes 20% off that.
       No separate water-glass line: standing guests take water in the same
       tall glass as juice and sodas, and the Low Tumbler is the rocks glass.
     Long events add 8% (6-7h) or 15% (8h+), capped at 3.0 per guest.

     SEATED / sit-down events are simpler and are quoted PER COVER: one of
     each glass in service on the setting plus spares, one water glass per
     cover, and a small shared allowance for reception / bar glassware. A
     formal dinner therefore runs higher (water + red + white + flute is
     4 per cover). A full setting is laid whether or not staff are washing,
     so the washing question is not asked on a seated event.

     Every type is also capped at the number of pours + 5% - you can never
     need more glasses than drinks served.
*/
(function () {
  // Standing / cocktail-table budget: drinks glasses per guest.
  var STANDING = { base: 2.2, cap: 3.0 };
  // Glasses are washed and re-used during the event, so fewer are needed.
  var WASHING_FACTOR = 0.8;
  // Water on a seated setting: one Low Tumbler per cover, quoted on its own
  // rather than out of the drinks budget. Standing events get no separate
  // water line - the tall glass already serves water, juice and sodas.
  var WATER_PER_COVER = 1.00;
  // Seated events: the drinks glasses that go ON the setting, per cover.
  var SEATED_SETTING = {
    'Red Wine Glass': 0.65,
    'White Wine Glass': 0.65,
    'Champagne Flute': 0.80,   // 0.30 when there is no toast
    'Moroccan Teacup': 0.50
  };
  var SEATED_SPARES = 1.08;      // breakage and re-sets
  // Everything else at a seated event is reception / bar glassware, which is
  // standing-event glassware: a small shared budget, split by what is poured.
  var SEATED_BAR_BUDGET = 0.7;   // glasses per guest across the bar types
  var POUR_CEILING = 1.05;       // never quote more glasses than pours + 5%
  var TYPE_FLOOR = 10;           // smallest sensible rental quantity of a type

  // How this event's drinks mix compares with a standard bar. Used only to
  // trim a place setting on a seated event (a beer-light bar gets fewer beer
  // glasses on the table); standing events are split by actual pours instead.
  var ASSUMED = { wine: 0.60, beer: 0.40, mixed: 0.225 };
  var MIX_CLAMP = { min: 0.4, max: 1.6 };
  function mixFactor(actualShare, assumedShare) {
    if (!actualShare) return 0;
    return Math.max(MIX_CLAMP.min, Math.min(MIX_CLAMP.max, actualShare / assumedShare));
  }

  // The per-guest figures assume a normal 3-5 hour event.
  function budgetDuration(hours) {
    if (hours <= 5) return 1;
    if (hours <= 7) return 1.08;
    return 1.15;
  }

  function initCalculator(root) {
    if (!root || root.dataset.dcReady) return;
    root.dataset.dcReady = '1';

    var $ = function (id) { return root.querySelector('[data-dc="' + id + '"]'); };

    var state = {
      mode: null, guests: 50, duration: 4, eventType: 'dinner', barType: 'full',
      alcRatio: 70, prefWine: 50, prefBeer: 25, nonAlcStyle: 'standard',
      hotDrinks: { standard: true, arabicCoffee: true, turkishCoffee: false, arabicTea: false },
      climate: 'outdoor', washing: 'no', format: 'standing'
    };

    var eventModifiers = {
      brunch:   { rate: 0.8, champagneBoost: 0.6 },
      lunch:    { rate: 0.8, champagneBoost: 0 },
      dinner:   { rate: 1.1, champagneBoost: 0 },
      cocktail: { rate: 1.4, champagneBoost: 0 },
      wedding:  { rate: 1.3, champagneBoost: 1.0 }  // a toast is one glass per guest
    };

    var GLASS = {
      TUMBLER: 'Low Tumbler', HIGH_TUMBLER: 'High Tumbler', RED_WINE: 'Red Wine Glass',
      WHITE_WINE: 'White Wine Glass', CHAMPAGNE: 'Champagne Flute', BEER: 'Beer Glass',
      MARTINI: 'Martini Glass', MOROCCAN_TEACUP: 'Moroccan Teacup'
    };
    var LIMITED_STOCK = [GLASS.MARTINI, GLASS.MOROCCAN_TEACUP];
    var WATER_GLASS = GLASS.TUMBLER;   // the seated water glass
    // What each glass is actually for. The low tumbler is the water glass on a
    // seated setting and the rocks glass at a standing bar; the tall glass
    // carries water as well as juice and sodas when guests are standing.
    function glassUse(type, seated) {
      if (type === GLASS.TUMBLER) {
        return seated ? 'Water on the setting, plus spirits on the rocks'
                      : 'Spirits served neat or on the rocks';
      }
      if (type === GLASS.HIGH_TUMBLER) {
        return seated ? 'Reception drinks — mixed drinks, juice and sodas'
                      : 'Water, juice, sodas and mixed drinks';
      }
      if (type === GLASS.BEER) return 'Beer and NA beer';
      if (type === GLASS.CHAMPAGNE) return 'Sparkling and the toast';
      return '';
    }

    var lastList = null;

    root.querySelectorAll('.gate-card').forEach(function (card) {
      card.addEventListener('click', function () { state.mode = card.dataset.mode; showCalculator(); });
    });

    $('back').addEventListener('click', function () {
      $('step-calc').classList.add('dc-hidden');
      $('step-gate').classList.remove('dc-hidden');
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    function showCalculator() {
      $('step-gate').classList.add('dc-hidden');
      $('step-calc').classList.remove('dc-hidden');

      var badge = { 'alcoholic': 'With Alcohol', 'mixed': 'Mixed — Alcohol + Curated Non-Alc', 'non-alcoholic': 'Non-Alcoholic' };
      $('mode-badge').textContent = badge[state.mode];

      var showAlc = state.mode === 'alcoholic' || state.mode === 'mixed';
      var showNonAlc = state.mode === 'non-alcoholic' || state.mode === 'mixed';
      $('alc-fields').classList.toggle('dc-hidden', !showAlc);
      $('non-alc-fields').classList.toggle('dc-hidden', !showNonAlc);

      if (state.mode === 'mixed') {
        state.alcRatio = 55; $('alc-ratio').value = 55;
        state.nonAlcStyle = 'premium'; updateToggle('non-alc-style', 'premium');
      } else if (state.mode === 'alcoholic') {
        state.alcRatio = 70; $('alc-ratio').value = 70;
      }
      syncPrefs();
      calculate();
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function updateToggle(groupId, value) {
      var grp = $(groupId);
      if (!grp) return;
      grp.querySelectorAll('button').forEach(function (b) {
        b.classList.toggle('active', b.dataset.val === value);
      });
    }
    function bindToggle(groupId, stateKey) {
      var grp = $(groupId);
      if (!grp) return;
      grp.querySelectorAll('button').forEach(function (b) {
        b.addEventListener('click', function () {
          state[stateKey] = b.dataset.val;
          updateToggle(groupId, b.dataset.val);
          calculate();
        });
      });
    }
    function bindMultiChip(groupId, stateKey) {
      $(groupId).querySelectorAll('button').forEach(function (b) {
        b.addEventListener('click', function () {
          var key = b.dataset.val;
          state[stateKey][key] = !state[stateKey][key];
          b.classList.toggle('active', state[stateKey][key]);
          calculate();
        });
      });
    }
    function bindInput(id, stateKey, displayId, suffix) {
      var el = $(id);
      el.addEventListener('input', function () {
        state[stateKey] = Number(el.value);
        if (displayId) $(displayId).textContent = state[stateKey] + (suffix || '');
        syncPrefs();
        calculate();
      });
    }

    function syncPrefs() {
      var wine = Number($('pref-wine').value);
      var beer = Number($('pref-beer').value);
      if (wine + beer > 100) {
        beer = Math.max(0, beer - (wine + beer - 100));
        $('pref-beer').value = beer;
        state.prefBeer = beer;
      }
      var spirits = 100 - wine - beer;
      $('pref-wine-value').textContent = wine + '%';
      $('pref-beer-value').textContent = beer + '%';
      $('pref-spirits-value').textContent = spirits + '%';
      $('bar-alc').style.width = state.alcRatio + '%';
      $('bar-soft').style.width = (100 - state.alcRatio) + '%';
      $('alc-ratio-value').textContent = state.alcRatio + '%';
    }

    function calculate() {
      if (!state.mode) return;
      var g = state.guests, h = state.duration;
      if (!g || g < 1 || !h) {
        $('results').innerHTML = '<div class="empty-state">Enter guest count and duration to see estimates.</div>';
        $('total-servings').textContent = '—';
        $('email-panel').classList.add('dc-hidden');
        lastList = null;
        return;
      }

      var mod = eventModifiers[state.eventType];
      var totalDrinks = g * (mod.rate * h + 0.6);
      var climateMult = state.climate === 'outdoor' ? 1.3 : 1.0;
      var waterLitres = g * h * (state.climate === 'outdoor' ? 0.45 : 0.3);
      var iceKg = Math.ceil((g * h) / 3);
      var lemons = Math.ceil(g / 15);
      var limes = Math.ceil(g / 15);

      var isAlc = state.mode === 'alcoholic' || state.mode === 'mixed';
      var isNonAlc = state.mode === 'non-alcoholic' || state.mode === 'mixed';

      var alcDrinks = 0, softDrinks = 0;
      if (isAlc) {
        var alcPct = state.alcRatio / 100;
        alcDrinks = totalDrinks * alcPct;
        softDrinks = totalDrinks * (1 - alcPct) * climateMult;
      } else {
        softDrinks = totalDrinks * climateMult;
      }
      var allDrinks = Math.max(1, alcDrinks + softDrinks);

      // Glassware demand: pours per type, plus how this event's mix compares
      // with the mix a standard bar assumes.
      var glassware = {};
      var alcShareOfAll = alcDrinks / allDrinks;
      var softShareOfAll = softDrinks / allDrinks;
      var mix = { wine: 0, beer: 0, mixed: 0 };
      if (isAlc) {
        var wr = state.prefWine / 100, br = state.prefBeer / 100;
        var sr = Math.max(0, 1 - wr - br);
        if (state.barType === 'beer-wine') { var sm = (wr + br) || 1; wr = wr / sm; br = br / sm; sr = 0; }
        mix.wine = mixFactor(wr, ASSUMED.wine);
        mix.beer = mixFactor(br, ASSUMED.beer);
        mix.mixed = mixFactor(sr * alcShareOfAll + softShareOfAll, ASSUMED.mixed);
      } else {
        mix.mixed = mixFactor(softShareOfAll, ASSUMED.mixed);
      }

      function addGlass(type, pours, factor) {
        if (!pours || pours <= 0) return;
        var entry = glassware[type] || (glassware[type] = { pours: 0, factor: 0 });
        entry.pours += pours;
        entry.factor = Math.max(entry.factor, factor == null ? 1 : factor);
      }

      var html = '';
      var total = 0;
      var plain = [];
      function push(group, label, qty, unit) {
        plain.push(group + ' | ' + label + ': ' + qty + (unit ? ' ' + unit : ''));
      }

      // ---------- Alcohol ----------
      if (isAlc) {
        var wineRatio = state.prefWine / 100;
        var beerRatio = state.prefBeer / 100;
        var spiritsRatio = 1 - wineRatio - beerRatio;
        if (state.barType === 'beer-wine') {
          var sum = (wineRatio + beerRatio) || 1;
          wineRatio = wineRatio / sum;
          beerRatio = beerRatio / sum;
          spiritsRatio = 0;
        }

        var wineDrinks = alcDrinks * wineRatio;
        var beerDrinks = alcDrinks * beerRatio;
        var spiritDrinks = alcDrinks * spiritsRatio;
        var champagneDrinks = g * mod.champagneBoost;

        var wineBottles = Math.ceil(wineDrinks / 5);
        var champagneBottles = Math.ceil(champagneDrinks / 6);
        var beerBottles = Math.ceil(beerDrinks);
        var spiritsBottles = Math.ceil(spiritDrinks / 16);
        var mixerBottles = Math.ceil(spiritDrinks / 8);
        var garnish = state.barType === 'full' ? Math.ceil(g / 20) : 0;

        html += '<div class="result-group"><h3>Alcohol</h3>';
        if (champagneBottles > 0) {
          html += row('Champagne / Sparkling', champagneBottles, 'bottles', '750ml · welcome pours', GLASS.CHAMPAGNE);
          push('Alcohol', 'Champagne / sparkling', champagneBottles, 'bottles');
          addGlass(GLASS.CHAMPAGNE, champagneDrinks, 1); // the toast reaches everyone
        }
        if (wineBottles > 0) {
          var redBottles = Math.ceil(wineBottles / 2);
          var whiteBottles = wineBottles - redBottles;
          html += row('Red wine', redBottles, 'bottles', '750ml', GLASS.RED_WINE);
          html += row('White / rosé wine', whiteBottles, 'bottles', '750ml', GLASS.WHITE_WINE);
          push('Alcohol', 'Red wine', redBottles, 'bottles');
          push('Alcohol', 'White / rose wine', whiteBottles, 'bottles');
          addGlass(GLASS.RED_WINE, wineDrinks / 2, mix.wine);
          addGlass(GLASS.WHITE_WINE, wineDrinks / 2, mix.wine);
        }
        if (beerBottles > 0) {
          html += row('Beer', beerBottles, 'bottles/cans', '330ml · mix lagers and a light option', GLASS.BEER);
          push('Alcohol', 'Beer', beerBottles, 'bottles/cans');
          addGlass(GLASS.BEER, beerDrinks, mix.beer);
        }
        if (spiritsBottles > 0) {
          html += row('Spirits', spiritsBottles, 'bottles', '750ml · vodka, gin, whisky, rum', GLASS.TUMBLER + ' + ' + GLASS.HIGH_TUMBLER);
          html += row('Mixers', mixerBottles, 'litres', 'tonic, soda, cola, ginger ale', '—');
          push('Alcohol', 'Spirits', spiritsBottles, 'bottles');
          push('Alcohol', 'Mixers', mixerBottles, 'litres');
          addGlass(GLASS.HIGH_TUMBLER, spiritDrinks * 0.6, mix.mixed);
          addGlass(GLASS.TUMBLER, spiritDrinks * 0.3, 1);
          if (state.eventType === 'cocktail' || state.eventType === 'wedding') {
            addGlass(GLASS.MARTINI, spiritDrinks * 0.1, mix.mixed);
          }
        }
        html += '</div>';

        html += '<div class="result-group"><h3>Bar Essentials</h3>';
        html += row('Ice', iceKg, 'kg', 'drinks &amp; chilling', '—');
        html += row('Lemons', lemons, '', '', '—');
        html += row('Limes', limes, '', '', '—');
        push('Bar essentials', 'Ice', iceKg, 'kg');
        push('Bar essentials', 'Lemons', lemons, '');
        push('Bar essentials', 'Limes', limes, '');
        if (garnish > 0) {
          html += row('Garnish (olives, mint, berries)', garnish, 'portions', '', '—');
          push('Bar essentials', 'Garnish', garnish, 'portions');
        }
        html += '</div>';

        total += Math.round(alcDrinks + champagneDrinks);
      }

      // ---------- Non-alcoholic ----------
      if (state.mode === 'non-alcoholic' || state.mode === 'mixed') {
        var styleMix = {
          simple:   { soda: 0.55, juice: 0.30, mocktail: 0.00, premiumMixer: 0.15, naBeerWine: 0.00 },
          standard: { soda: 0.40, juice: 0.30, mocktail: 0.15, premiumMixer: 0.15, naBeerWine: 0.00 },
          premium:  { soda: 0.20, juice: 0.20, mocktail: 0.35, premiumMixer: 0.15, naBeerWine: 0.10 }
        }[state.nonAlcStyle];

        var totalSoft = softDrinks;
        var sodaDrinks = totalSoft * styleMix.soda;
        var juiceDrinks = totalSoft * styleMix.juice;
        var mocktailDrinks = totalSoft * styleMix.mocktail;
        var premMixerDrinks = totalSoft * styleMix.premiumMixer;
        var naBeerWineDrinks = totalSoft * styleMix.naBeerWine;

        html += '<div class="result-group"><h3>Non-Alcoholic</h3>';

        var sodaCans = Math.ceil(sodaDrinks);
        var juiceLitres = Math.ceil(juiceDrinks * 0.25);
        if (sodaCans > 0) {
          html += row('Sodas', sodaCans, 'cans', '330ml · cola, lemon-lime, ginger ale, diet', GLASS.HIGH_TUMBLER);
          push('Non-alcoholic', 'Sodas', sodaCans, 'cans');
          addGlass(GLASS.HIGH_TUMBLER, sodaDrinks, mix.mixed);
        }
        if (juiceLitres > 0) {
          html += row('Juices', juiceLitres, 'litres', 'orange, apple, cranberry, mango', GLASS.HIGH_TUMBLER);
          push('Non-alcoholic', 'Juices', juiceLitres, 'litres');
          addGlass(GLASS.HIGH_TUMBLER, juiceDrinks, mix.mixed);
        }
        if (premMixerDrinks > 0) {
          var premBottles = Math.ceil(premMixerDrinks / 4);
          html += row('Premium mixers &amp; tonics', premBottles, 'bottles', '1L · artisan tonic, elderflower, grapefruit', GLASS.TUMBLER);
          push('Non-alcoholic', 'Premium mixers and tonics', premBottles, 'bottles');
          addGlass(GLASS.TUMBLER, premMixerDrinks, 1);
        }
        if (naBeerWineDrinks > 0) {
          var naBottles = Math.ceil(naBeerWineDrinks);
          html += row('NA beer &amp; wine', naBottles, 'bottles', 'adds a grown-up non-alc option', GLASS.BEER + ' / ' + GLASS.WHITE_WINE);
          push('Non-alcoholic', 'NA beer and wine', naBottles, 'bottles');
          addGlass(GLASS.BEER, naBeerWineDrinks * 0.6, mixFactor(naBeerWineDrinks / allDrinks, ASSUMED.beer));
          addGlass(GLASS.WHITE_WINE, naBeerWineDrinks * 0.4, mixFactor(naBeerWineDrinks / allDrinks, ASSUMED.wine));
        }
        if (mocktailDrinks > 0) {
          var mocktailCount = Math.ceil(mocktailDrinks);
          var varieties = Math.max(2, Math.min(5, Math.ceil(g / 30)));
          html += row('Signature mocktails', mocktailCount, 'servings', 'suggest ' + varieties + ' varieties · e.g. virgin mojito, berry spritz, passionfruit fizz', GLASS.HIGH_TUMBLER + ' + ' + GLASS.MARTINI);
          push('Non-alcoholic', 'Signature mocktails', mocktailCount, 'servings');
          addGlass(GLASS.HIGH_TUMBLER, mocktailDrinks * 0.75, mix.mixed);
          addGlass(GLASS.MARTINI, mocktailDrinks * 0.25, mix.mixed);
          html += row('Fresh herbs (mint, basil, rosemary)', Math.ceil(g / 10), 'bunches', '', '—');
          html += row('Berries &amp; fruit garnish', Math.ceil(g / 8), 'portions', '', '—');
          push('Non-alcoholic', 'Fresh herbs', Math.ceil(g / 10), 'bunches');
          push('Non-alcoholic', 'Berries and fruit garnish', Math.ceil(g / 8), 'portions');
        }
        html += '</div>';

        var anyHotSelected = state.hotDrinks.standard || state.hotDrinks.arabicCoffee || state.hotDrinks.turkishCoffee || state.hotDrinks.arabicTea;
        total += Math.round(totalSoft + (anyHotSelected ? g * 0.6 : 0));
      } else if (state.mode === 'alcoholic') {
        var softCans = Math.ceil(softDrinks);
        html += '<div class="result-group"><h3>Soft Drinks</h3>';
        html += row('Sodas &amp; juices', softCans, 'cans/drinks', '330ml · variety', GLASS.HIGH_TUMBLER);
        push('Soft drinks', 'Sodas and juices', softCans, 'cans/drinks');
        addGlass(GLASS.HIGH_TUMBLER, softDrinks, mix.mixed);
        html += '</div>';
        total += Math.round(softDrinks);
      }

      // ---------- Water (always) ----------
      var waterBottles = Math.ceil(waterLitres / 0.5);
      var sparklingBottles = Math.ceil(waterBottles * ((isNonAlc && !isAlc) ? 0.5 : 0.4));
      html += '<div class="result-group"><h3>Water</h3>';
      html += row('Still water', waterBottles, 'bottles', '500ml', GLASS.TUMBLER);
      html += row('Sparkling water', sparklingBottles, 'bottles', '500ml', GLASS.TUMBLER);
      html += '</div>';
      push('Water', 'Still water', waterBottles, 'bottles');
      push('Water', 'Sparkling water', sparklingBottles, 'bottles');
      // Water glasses are a seated-only line, handled in renderGlassware.

      // ---------- Hot drinks ----------
      if (state.mode === 'non-alcoholic' || state.mode === 'mixed') {
        var hd = state.hotDrinks;
        if (hd.standard || hd.arabicCoffee || hd.turkishCoffee || hd.arabicTea) {
          html += '<div class="result-group"><h3>Hot Drinks Station</h3>';
          if (hd.standard) {
            var coffeeServes = Math.ceil(g * 0.5);
            var teaServes = Math.ceil(g * 0.3);
            html += row('Coffee (filter / espresso)', coffeeServes, 'servings', 'allow for ~50% of guests', 'Teacup or mug — not in Party Social inventory');
            html += row('Tea (black, green, herbal)', teaServes, 'bags', 'assorted', 'Teacup or mug — not in Party Social inventory');
            html += row('Milk', Math.ceil(g * 0.05), 'litres', 'dairy + an alt milk option', '—');
            html += row('Sugar &amp; sweetener', Math.ceil(g / 10), 'portions', '', '—');
            push('Hot drinks', 'Coffee', coffeeServes, 'servings');
            push('Hot drinks', 'Tea', teaServes, 'bags');
            push('Hot drinks', 'Milk', Math.ceil(g * 0.05), 'litres');
          }
          if (hd.arabicCoffee) {
            var gahwa = Math.ceil(g * 0.6);
            html += row('Arabic coffee (gahwa)', gahwa, 'servings', 'cardamom-spiced · served with dates', 'Finjan (Arabic cup) — not in Party Social inventory');
            html += row('Dates (to accompany)', Math.ceil(g * 1.5), 'pieces', 'traditional pairing', '—');
            push('Hot drinks', 'Arabic coffee (gahwa)', gahwa, 'servings');
            push('Hot drinks', 'Dates', Math.ceil(g * 1.5), 'pieces');
          }
          if (hd.turkishCoffee) {
            var turkish = Math.ceil(g * 0.4);
            html += row('Turkish coffee', turkish, 'servings', 'finely ground · prepared in cezve', 'Turkish coffee cup (fincan) — not in Party Social inventory');
            push('Hot drinks', 'Turkish coffee', turkish, 'servings');
          }
          if (hd.arabicTea) {
            var mintTea = Math.ceil(g * 0.5);
            html += row('Arabic tea (mint tea)', mintTea, 'servings', 'sweet green tea with fresh mint', GLASS.MOROCCAN_TEACUP);
            addGlass(GLASS.MOROCCAN_TEACUP, mintTea, 0.5); // about half the room takes tea
            html += row('Fresh mint (for tea)', Math.ceil(g / 12), 'bunches', '', '—');
            push('Hot drinks', 'Arabic tea', mintTea, 'servings');
          }
          html += '</div>';
        }
      }

      // ---------- Bar essentials (non-alc only) ----------
      if (state.mode === 'non-alcoholic') {
        html += '<div class="result-group"><h3>Bar Essentials</h3>';
        html += row('Ice', iceKg, 'kg', 'drinks &amp; chilling', '—');
        push('Bar essentials', 'Ice', iceKg, 'kg');
        if (state.nonAlcStyle !== 'simple') {
          html += row('Lemons', lemons, '', '', '—');
          html += row('Limes', limes, '', '', '—');
          push('Bar essentials', 'Lemons', lemons, '');
          push('Bar essentials', 'Limes', limes, '');
        }
        html += '</div>';
      }

      html += renderGlassware(glassware, plain, g, h);

      $('total-servings').textContent = total;
      $('results').innerHTML = html;

      lastList = {
        mode: state.mode, guests: g, hours: h, eventType: state.eventType,
        climate: state.climate, format: state.format,
        washing: state.washing, totalServings: total, lines: plain
      };
      if (root.dataset.klaviyoKey) $('email-panel').classList.remove('dc-hidden');
    }

    function row(label, qty, unit, sub, glass) {
      var subHtml = sub ? '<span class="sub">' + sub + '</span>' : '';
      var glassHtml = (glass && glass !== '—') ? '<span class="glass-tag">Serve in: ' + glass + '</span>' : '';
      return '<div class="result-item">' +
        '<div><div class="label">' + label + '</div>' + subHtml + glassHtml + '</div>' +
        '<div class="qty">' + qty + (unit ? ' <span class="unit">' + unit + '</span>' : '') + '</div>' +
        '</div>';
    }

    function renderGlassware(glassware, plain, guests, hours) {
      var order = [GLASS.TUMBLER, GLASS.HIGH_TUMBLER, GLASS.WHITE_WINE, GLASS.RED_WINE, GLASS.CHAMPAGNE, GLASS.BEER, GLASS.MARTINI, GLASS.MOROCCAN_TEACUP];
      var seated = state.format === 'seated';
      var washing = state.washing === 'yes' && !seated;

      var inUse = order.filter(function (t) { var e = glassware[t]; return e && e.pours > 0; });
      if (!inUse.length) return '';

      var alloc = {};
      var totalGlasses = 0;
      var perGuestTarget = 0;

      // Split a budget of glasses across types in proportion to what is
      // actually being poured, respecting a minimum usable quantity of each
      // and never exceeding the number of pours. Floors are paid first so the
      // budget is not quietly overshot.
      function splitBudget(types, budget) {
        var floors = {}, fixed = 0, flexPours = 0;
        var minQty = Math.max(TYPE_FLOOR, guests * 0.06);
        var sum = 0;
        types.forEach(function (t) { sum += glassware[t].pours; });
        types.forEach(function (t) {
          var e = glassware[t];
          var share = budget * (e.pours / sum);
          var floorQty = Math.min(e.pours, minQty);
          if (share < floorQty) { floors[t] = floorQty; fixed += floorQty; }
          else flexPours += e.pours;
        });
        var rest = Math.max(0, budget - fixed);
        types.forEach(function (t) {
          var e = glassware[t];
          var want = floors[t] != null ? floors[t] : (flexPours ? rest * (e.pours / flexPours) : 0);
          var qty = Math.ceil(Math.min(e.pours * POUR_CEILING, want));
          if (qty > 0) { alloc[t] = (alloc[t] || 0) + qty; totalGlasses += qty; }
        });
      }

      if (seated) {
        // The place setting, per cover...
        var toast = (eventModifiers[state.eventType] || {}).champagneBoost > 0;
        var barTypes = [];
        inUse.forEach(function (type) {
          var cover = SEATED_SETTING[type];
          if (cover == null) { barTypes.push(type); return; }
          if (type === GLASS.CHAMPAGNE && !toast) cover = 0.30;
          var entry = glassware[type];
          // A wine-light bar does not get a full setting of that glass.
          var f = Math.max(0.5, Math.min(1.15, entry.factor || 1));
          var qty = Math.ceil(Math.min(entry.pours * POUR_CEILING, guests * cover * f * SEATED_SPARES));
          if (qty > 0) { alloc[type] = qty; totalGlasses += qty; }
        });
        // ...and a shared allowance for reception / bar glassware on top.
        if (barTypes.length) splitBudget(barTypes, guests * SEATED_BAR_BUDGET);
      } else {
        // Standing: one per-guest budget for drinks glasses, split across
        // every type in use. Washing and re-using means fewer are needed.
        perGuestTarget = Math.min(STANDING.cap, STANDING.base * budgetDuration(hours));
        if (washing) perGuestTarget = perGuestTarget * WASHING_FACTOR;
        splitBudget(inUse, guests * perGuestTarget);
      }

      // Water glasses are their own line and only on a seated setting:
      // one per cover, plus spares. Standing events pour from bottles.
      if (seated) {
        var waterQty = Math.ceil(guests * WATER_PER_COVER * SEATED_SPARES);
        if (waterQty > 0) {
          alloc[WATER_GLASS] = (alloc[WATER_GLASS] || 0) + waterQty;
          totalGlasses += waterQty;
        }
      }

      var limited = [];
      var rows = order.map(function (type) {
        var qty = alloc[type];
        if (!qty) return '';
        var isLimited = LIMITED_STOCK.indexOf(type) !== -1;
        if (isLimited) limited.push(type);
        var unit = type === GLASS.MOROCCAN_TEACUP ? 'teacups' : 'glasses';
        var use = glassUse(type, seated);
        var sub = isLimited
          ? (use ? use + ' · limited stock, confirm on quote' : 'Limited stock — please confirm availability on quote')
          : use;
        plain.push('Glassware | ' + type + ': ' + qty + ' ' + unit);
        return row(type, qty, unit, sub, '—');
      }).join('');

      var perGuest = guests ? (totalGlasses / guests).toFixed(1) : '0';
      var limitedNote = limited.length
        ? ' <strong>' + limited.join(' and ') + '</strong> are in limited stock — confirm on quote.'
        : '';

      var note;
      if (seated) {
        note = 'Quoted the way a sit-down event is quoted — <strong>per place setting</strong>, one of each glass in service per cover, plus spares and a reception allowance. '
             + 'That is about <strong>' + perGuest + ' glasses per guest</strong>, ' + totalGlasses + ' in total. '
             + 'Water is one glass per cover, included in the ' + WATER_GLASS + ' count. ';
      } else {
        note = 'Quoted the way a standing / cocktail-table event is quoted — <strong>per guest, not per pour</strong>. '
             + 'Guests keep a glass and swap when they change drink, and not everyone drinks every category, so the working figure is a little over 2 glasses per guest, '
             + 'split here across the types you are actually pouring. That is <strong>' + perGuest + ' glasses per guest</strong>, ' + totalGlasses + ' in total. '
             + 'Water is poured into the same tall glass as juice and sodas, so there is no separate water-glass line. '
             + (washing
                 ? 'Staff are washing and re-using during the event, so this is 20% below what an unwashed event needs. '
                 : '');
      }
      note = '<p class="glass-note">' + note + 'All items above are available through <strong>Party Social</strong>.' + limitedNote + '</p>';

      return '<div class="result-group glassware-group"><h3>Glassware to Rent</h3>' + rows + note + '</div>';
    }

    // ---------- Email the list (Klaviyo) ----------
    var sendBtn = $('email-send');
    if (sendBtn) {
      sendBtn.addEventListener('click', function () {
        var key = root.dataset.klaviyoKey;
        var listId = root.dataset.klaviyoList;
        var input = $('email-input');
        var msg = $('email-msg');
        var email = (input.value || '').trim();

        function say(text, isError) {
          msg.textContent = text;
          msg.classList.remove('dc-hidden');
          msg.classList.toggle('error', !!isError);
        }

        if (!email || email.indexOf('@') < 1) { say('Please enter a valid email address.', true); input.focus(); return; }
        if (!key || !listId || !lastList) { say('We could not send that just now — please request a quote instead.', true); return; }

        sendBtn.disabled = true;
        sendBtn.textContent = 'Sending…';

        var payload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    properties: {
                      drinks_calculator_mode: lastList.mode,
                      drinks_calculator_guests: lastList.guests,
                      drinks_calculator_hours: lastList.hours,
                      drinks_calculator_event_type: lastList.eventType,
                      drinks_calculator_climate: lastList.climate,
                      drinks_calculator_total_servings: lastList.totalServings,
                      drinks_calculator_list: lastList.lines.join('\n'),
                      drinks_calculator_submitted_at: new Date().toISOString()
                    }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        };

        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
          body: JSON.stringify(payload)
        }).then(function (res) {
          if (res.ok || res.status === 202) {
            say('✓ On its way — check your inbox.', false);
            input.disabled = true;
            sendBtn.textContent = 'Sent';
          } else {
            sendBtn.disabled = false;
            sendBtn.textContent = 'Send List';
            say('We could not send that just now — please request a quote instead.', true);
          }
        }).catch(function () {
          sendBtn.disabled = false;
          sendBtn.textContent = 'Send List';
          say('We could not send that just now — please request a quote instead.', true);
        });
      });
    }

    var noBtn = $('glass-no');
    if (noBtn) {
      noBtn.addEventListener('click', function () { $('glass-panel').classList.add('dc-hidden'); });
    }

    bindInput('guests', 'guests');
    bindInput('duration', 'duration', 'duration-value', ' hrs');
    bindInput('alc-ratio', 'alcRatio', 'alc-ratio-value', '%');
    bindInput('pref-wine', 'prefWine', 'pref-wine-value', '%');
    bindInput('pref-beer', 'prefBeer', 'pref-beer-value', '%');
    bindToggle('event-type', 'eventType');
    bindToggle('bar-type', 'barType');
    bindToggle('climate', 'climate');
    bindToggle('washing', 'washing');
    // Washing only changes a standing quote: a seated event lays a full
    // setting per cover either way, so the question is hidden there rather
    // than asked for nothing.
    var washingField = $('washing-field');
    function syncWashingField() {
      if (!washingField) return;
      var seated = state.format === 'seated';
      washingField.classList.toggle('dc-hidden', seated);
      if (seated && state.washing !== 'no') {
        state.washing = 'no';
        updateToggle('washing', 'no');
      }
    }
    var formatGroup = $('format');
    if (formatGroup) formatGroup.querySelectorAll('button').forEach(function (b) {
      b.addEventListener('click', function () {
        state.format = b.dataset.val;
        updateToggle('format', state.format);
        syncWashingField();
        calculate();
      });
    });
    syncWashingField();
    bindToggle('non-alc-style', 'nonAlcStyle');
    bindMultiChip('hot-drinks', 'hotDrinks');

    syncPrefs();
  }

  function boot() {
    document.querySelectorAll('.dc-page').forEach(initCalculator);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
